import { useQuery } from '@tanstack/react-query';
import {
  GetBackProductionLotAutoAssignWorkersHistoricalParallelPairsRequest,
  HistoricalParallelMachinePairView,
  HistoricalParallelMachinePairsView,
  useAPI,
} from '@nexelem/api';

export const historicalParallelMachinePairsQueryKey = (
  request: GetBackProductionLotAutoAssignWorkersHistoricalParallelPairsRequest,
) => ['auto-assign-historical-parallel-pairs', request];

const pairKey = (a: number, b: number) => (a < b ? `${a}-${b}` : `${b}-${a}`);

const buildPairFrequencyMap = (pairs: HistoricalParallelMachinePairView[]) =>
  new Map(pairs.map((pair) => [pairKey(pair.firstMachineId, pair.secondMachineId), pair.frequency]));

export type HistoricalParallelMachinePairsMatrixData = {
  machines: NonNullable<HistoricalParallelMachinePairsView['machines']>;
  maxFrequency: number;
  getFrequency: (firstMachineId: number, secondMachineId: number) => number;
};

const toPairFrequencyMatrix = (data: HistoricalParallelMachinePairsView): HistoricalParallelMachinePairsMatrixData => {
  const machines = data.machines ?? [];
  const pairs = data.pairs ?? [];
  const frequencyMap = buildPairFrequencyMap(pairs);
  const maxFrequency = pairs.reduce((max, pair) => Math.max(max, pair.frequency), 0);

  return {
    machines,
    maxFrequency,
    getFrequency: (firstMachineId, secondMachineId) => frequencyMap.get(pairKey(firstMachineId, secondMachineId)) ?? 0,
  };
};

export const useHistoricalParallelMachinePairs = (
  request: GetBackProductionLotAutoAssignWorkersHistoricalParallelPairsRequest,
) => {
  const api = useAPI();

  return useQuery({
    queryKey: historicalParallelMachinePairsQueryKey(request),
    queryFn: () => api.getBackProductionLotAutoAssignWorkersHistoricalParallelPairs(request),
    select: toPairFrequencyMatrix,
    staleTime: 60_000,
  });
};
